import { useMemo, useState } from 'react';
import { VERBATIM_DATA } from '../../data/verbatimData';
import { FEATURES } from '../../data/conjointData';
import { InfoButton } from '../common/Tooltip';
import { Users } from 'lucide-react';

type DemoDimension = 'gender' | 'age' | 'income' | 'region' | 'goal';

const DIMENSIONS: { key: DemoDimension; label: string; groups: { value: string; label: string; filter: (r: typeof VERBATIM_DATA[0]) => boolean }[] }[] = [
  {
    key: 'gender', label: 'Gender',
    groups: [
      { value: 'Male', label: 'Male', filter: r => r.gender === 'Male' },
      { value: 'Female', label: 'Female', filter: r => r.gender === 'Female' },
    ],
  },
  {
    key: 'age', label: 'Age',
    groups: [
      { value: '18-34', label: '18-34', filter: r => r.age >= 18 && r.age <= 34 },
      { value: '35-49', label: '35-49', filter: r => r.age >= 35 && r.age <= 49 },
      { value: '50+', label: '50+', filter: r => r.age >= 50 },
    ],
  },
  {
    key: 'income', label: 'Income',
    groups: [
      { value: 'under50k', label: '<$50K', filter: r => r.income.includes('Under') || r.income.includes('Less') || r.income.match(/\$[0-4]/) !== null },
      { value: '50k-100k', label: '$50K-$100K', filter: r => r.income.includes('50,000') || r.income.includes('75,000') },
      { value: '100k+', label: '$100K+', filter: r => r.income.includes('100,000') || r.income.includes('125,000') || r.income.includes('150,000') || r.income.includes('200,000') || r.income.includes('more') },
    ],
  },
  {
    key: 'region', label: 'Region',
    groups: [
      { value: 'Northeast', label: 'NE', filter: r => r.region === 'Northeast' },
      { value: 'Southeast', label: 'SE', filter: r => r.region === 'Southeast' },
      { value: 'Midwest', label: 'MW', filter: r => r.region === 'Midwest' },
      { value: 'West', label: 'West', filter: r => r.region === 'West' },
    ],
  },
  {
    key: 'goal', label: 'Health Goal',
    groups: [
      { value: 'weight', label: 'Weight', filter: r => r.primaryGoal.toLowerCase().includes('weight') },
      { value: 'health', label: 'Health', filter: r => r.primaryGoal.toLowerCase().includes('health') || r.primaryGoal.toLowerCase().includes('longevity') },
      { value: 'strength', label: 'Strength', filter: r => r.primaryGoal.toLowerCase().includes('strength') || r.primaryGoal.toLowerCase().includes('muscle') || r.primaryGoal.toLowerCase().includes('fitness') },
    ],
  },
];

function computeFeaturePrefs(respondents: typeof VERBATIM_DATA) {
  const n = respondents.length;
  if (n === 0) return FEATURES.map(f => ({ name: f.name, avgRank: 0, importantPct: 0 }));

  return FEATURES.map(f => {
    let rankSum = 0, rankCount = 0, importantCount = 0;
    for (const r of respondents) {
      const rank = r.featureRanking[f.name];
      if (rank > 0) { rankSum += rank; rankCount++; }
      const imp = r.featureImportance[f.name];
      if (imp === 'Very important' || imp === 'Extremely important') importantCount++;
    }
    return {
      name: f.name,
      avgRank: rankCount > 0 ? +(rankSum / rankCount).toFixed(1) : 0,
      importantPct: Math.round(importantCount / n * 100),
    };
  });
}

export function WTPByDemographic() {
  const [dimension, setDimension] = useState<DemoDimension>('age');
  const [metric, setMetric] = useState<'rank' | 'importance'>('importance');

  const selectedDim = DIMENSIONS.find(d => d.key === dimension)!;

  const overallPrefs = useMemo(() => computeFeaturePrefs(VERBATIM_DATA), []);

  const groupData = useMemo(() => {
    return selectedDim.groups.map(group => {
      const filtered = VERBATIM_DATA.filter(group.filter);
      const prefs = computeFeaturePrefs(filtered);
      return { ...group, count: filtered.length, prefs };
    });
  }, [selectedDim]);

  const featureNames = FEATURES.map(f => f.name);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Users size={16} className="text-chart-purple" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Feature Preferences by Demographic</h3>
        <InfoButton helpKey="demographicCrossTab" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        How feature preferences vary across demographic groups. Based on stated importance (Q5) and rankings (Q6), not conjoint-derived WTP.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-micro text-muted">Dimension:</span>
          <select
            value={dimension}
            onChange={e => setDimension(e.target.value as DemoDimension)}
            className="text-xs px-2 py-1 rounded border border-border bg-transparent text-primary"
          >
            {DIMENSIONS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-micro text-muted">Show:</span>
          <select
            value={metric}
            onChange={e => setMetric(e.target.value as 'rank' | 'importance')}
            className="text-xs px-2 py-1 rounded border border-border bg-transparent text-primary"
          >
            <option value="importance">% Very/Extremely Important</option>
            <option value="rank">Average Rank (1=top)</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2 font-medium text-secondary">Feature</th>
              <th className="text-right py-2 px-2 font-medium text-secondary">Overall</th>
              {groupData.map(g => (
                <th key={g.value} className="text-right py-2 px-2 font-medium text-secondary">
                  {g.label}
                  <div className="text-micro text-muted font-normal">n=<span className="font-mono">{g.count}</span></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureNames.map((fname, fi) => {
              const overall = overallPrefs[fi];
              const overallVal = metric === 'importance' ? overall.importantPct : overall.avgRank;

              return (
                <tr key={fname} className="border-b border-border/50">
                  <td className="py-2 px-2 text-primary font-medium">{fname}</td>
                  <td className="py-2 px-2 text-right font-mono text-secondary">
                    {metric === 'importance' ? `${overallVal}%` : `#${overallVal}`}
                  </td>
                  {groupData.map(g => {
                    const pref = g.prefs.find(p => p.name === fname);
                    const val = metric === 'importance' ? (pref?.importantPct ?? 0) : (pref?.avgRank ?? 0);
                    const delta = metric === 'importance' ? val - (overall.importantPct) : (overall.avgRank) - val;
                    const isHigh = metric === 'importance' ? delta > 5 : delta > 0.3;
                    const isLow = metric === 'importance' ? delta < -5 : delta < -0.3;

                    return (
                      <td key={g.value} className={`py-2 px-2 text-right font-mono ${isHigh ? 'text-metric-positive font-semibold' : isLow ? 'text-metric-negative font-semibold' : 'text-secondary'}`}>
                        {metric === 'importance' ? `${val}%` : `#${val}`}
                        {(isHigh || isLow) && (
                          <span className="text-micro ml-0.5">
                            {metric === 'importance'
                              ? `(${delta > 0 ? '+' : ''}${delta}pp)`
                              : `(${delta > 0 ? '+' : ''}${delta.toFixed(1)})`}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-micro text-muted mt-3 italic">
        Green = above population average. Red = below. Based on stated survey responses (Q5/Q6), not conjoint-derived WTP. Demographic groups with different feature priorities suggest different messaging strategies per segment.
      </p>
    </div>
  );
}
